import { ElectronPrintSettings } from '@interfaces/general';
import { Printers } from '@pages/Preferences/components/MultiPrinters';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { PRINT_AUTO_OPTIONS_MAP, PRINT_TEXT_SIZE_MAP, TICKET_TYPE_MAP } from '@utils/constants';

const getSafeLocalStorage = (key: string): string | null => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem(key);
    }
  } catch (error) {
    // LocalStorage not available
  }
  return null;
};

const initialState = (): Record<keyof typeof Printers, ElectronPrintSettings> => {
  const keys = Object.keys(Printers) as (keyof typeof Printers)[];

  return keys.reduce(
    (acc, key) => {
      const fixedKey = key === Printers.Printer1.key ? '' : key;

      acc[key] = {
        electronPrinterName: getSafeLocalStorage(`${fixedKey}electronPrinterName`) || null,
        electronPrintLogo: !fixedKey
          ? getSafeLocalStorage(`${fixedKey}electronPrintLogo`) !== 'false'
          : getSafeLocalStorage(`${fixedKey}electronPrintLogo`) === 'true',
        electronPrintCommerceData: !fixedKey
          ? getSafeLocalStorage(`${fixedKey}electronPrintCommerceData`) !== 'false'
          : getSafeLocalStorage(`${fixedKey}electronPrintCommerceData`) === 'true',
        electronPrintClientNumber: !fixedKey
          ? getSafeLocalStorage(`${fixedKey}electronPrintClientNumber`) !== 'false'
          : getSafeLocalStorage(`${fixedKey}electronPrintClientNumber`) === 'true',
        electronPrintTextSize:
          (getSafeLocalStorage(`${fixedKey}electronPrintTextSize`) as keyof typeof PRINT_TEXT_SIZE_MAP) ||
          PRINT_TEXT_SIZE_MAP.small.key,
        electronRightMargin: Number(getSafeLocalStorage(`${fixedKey}electronRightMargin`)) || 0,
        electronAutoPrint:
          (getSafeLocalStorage(`${fixedKey}electronAutoPrint`) as keyof typeof PRINT_AUTO_OPTIONS_MAP) ||
          PRINT_AUTO_OPTIONS_MAP.onOrderAccepted.key,
        electronLeftMargin: Number(getSafeLocalStorage(`${fixedKey}electronLeftMargin`)) || 0,
        electronPrintNumber: Number(getSafeLocalStorage(`${fixedKey}electronPrintNumber`)) || 1,
        electronMultiPrintDelayBetweenPrints:
          Number(getSafeLocalStorage(`${fixedKey}electronMultiPrintDelayBetweenPrints`)) || 3.5,
        electronTicketType:
          (getSafeLocalStorage(`${fixedKey}electronTicketType`) as 'normal' | 'simplified') ||
          TICKET_TYPE_MAP.normal.key,
      };

      return acc;
    },
    {} as Record<keyof typeof Printers, ElectronPrintSettings>,
  );
};

export const electronPrintSettingsSlice = createSlice({
  name: 'electronPrintSettings',
  initialState: initialState(),
  reducers: {
    updateElectronPrintSettings: (
      state,
      action: PayloadAction<{ settings: Partial<ElectronPrintSettings>; printerKey: keyof typeof Printers }>,
    ) => {
      const { settings, printerKey } = action.payload;
      let newState = { ...state[printerKey] };

      Object.keys(settings).forEach((key) => {
        const settingKey = key as keyof ElectronPrintSettings;
        const value = settings[settingKey];

        if (value === null || value === undefined) {
          return;
        }

        newState = { ...newState, [settingKey]: value };

        localStorage.setItem(
          `${printerKey === Printers.Printer1.key ? '' : printerKey}${settingKey}`,
          value.toString(),
        );
      });

      return { ...state, [printerKey]: newState };
    },

    reset: () => initialState(),
  },
});
